import * as moment from "moment";

import { CurrencyType, EventType, ProductTaxType, VendorDiscountType } from "./Enums";

import { VendorBasicModel, VendorStoreModel } from './VendorModel';
import { CategoryModel, ThematicModel } from './CategoryModel';
import { ShippingProviderModel } from './ShippingModel';
import { IProductInfo, IQuantifiable } from "./Interfaces";
import { EventModel } from './EventModel';
import { CurrencyInfo } from '../currency-info';


export class TicketModel implements IProductInfo, IQuantifiable {

    public constructor(init?: Partial<Pick<TicketModel, keyof TicketModel>>) {

        if (init) {
            if (init.vendor) {
                init.vendor = new VendorBasicModel(init.vendor);
            }
            if (init.categories && init.categories.length) {
                init.categories = init.categories.map(x => new CategoryModel(x));
            }
            if (init.thematics && init.thematics.length) {
                init.thematics = init.thematics.map(x => new ThematicModel(x));
            }
            if (typeof init.saleDateStartAt === 'string') {
                init.saleDateStartAt = new Date(init.saleDateStartAt);
            }
            if (typeof init.saleDateEndAt === 'string') {
                init.saleDateEndAt = new Date(init.saleDateEndAt);
            }
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    vendorId: number;
    communityId?: number;
    eventId: number;

    sku: string;
    name: string;
    description: string;
    logoUrl: string;
    thumbnailUrl: string;

    terms: string;
    instructions: string;

    stock: number;

    // Precio proveedor
    vendorPrice = 0; // Sin IVA
    vendorPriceWithTax = 0;  // vendorPriceWithDiscount * 1.13

    // Precio a Givi
    vendorDiscountType = VendorDiscountType.Custom;
    vendorPriceWithDiscount = 0; // vendorPrice / 1.15

    // Precio de venta
    price = 0;
    plusPrice = 0;
    // Precio de venta SALE
    allowDiscount = false;
    discountPercent = 0;
    priceWithDiscount = 0;

    //Indica si el comercio asume los costos de comisión y validación
    isCommissionToVendor = false;
    // Comisión 10% (sin impuestos)
    commissionCost = 0;
    // Costo validación ($.50 all transactions)
    validationCost = 0;

    points = 0;

    quantityOfPlaces = 1;


    taxType = ProductTaxType.Exempt;
    taxPercent = 0;

    // Inventory is generic
    isGeneric = true;
    quantity = 1;

    isAvailable = true;
    isActive = true;
    isVisible = true;

    createdAt = new Date;
    updatedAt?: Date;

    saleDateStartAt: Date;
    saleDateEndAt: Date;

    deletedAt?: Date;

    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    vendor: VendorBasicModel;

    eventObj: EventModel;


    categories: CategoryModel[] = [];

    thematics: ThematicModel[] = [];

    stores: VendorStoreModel[] = [];


    // isOutOfStock: boolean;


    shippingProviderId: number;

    shippingProvider: ShippingProviderModel;


    // For purchase modal
    quantitySelected = 0;

    availableQuantity: number[] = [];

    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.vendor?.currencyType ?? CurrencyType.CRC);
    }

    get availableQuantityByTicket(): number {

        let quantity;

        if (!this.isGeneric) {

            // Valida stock por ticket
            quantity = this.stock - this.quantity;
        }
        else {

            // Valida stock por evento
            quantity = this.eventObj.stock - this.eventObj.quantity;
        }

        return quantity;
    }

    // Used to display in the form invoice
    get priceWithoutPlusWithTax(): number {

        let price = this.price - this.plusPrice;
        return price;
    }

    // Used to display in the form invoice
    get priceTotal(): number {

        // Precio en SALE o precio normal
        const price = this.allowDiscount ? this.priceWithDiscount : this.price;

        return price * this.quantitySelected;
    }

    // Used to display in the form invoice
    get pointsTotal(): number {

        return this.points * this.quantitySelected;
    }

    // Used to sort ticket with more efficient
    get saleDateEndAtEvent(): Date {

        return this.eventObj?.endDateAt;
    }

    get isOutOfStock(): boolean {

        // Si es genérico no tiene limite de cantidad
        if (this.isGeneric) {
            return false;
        }

        // Valida si el aforo es ilimitado
        if (this.stock == 0) {
            // Valida si la cantidad comprada es mayor o igual al stock del evento
            if (this.eventObj.stock > 0) {
                if (this.eventObj.stock > 0 && this.quantity >= this.eventObj.stock) {

                    return true;
                }
            }
            // El aforo es ilimitado 
            return false;
        }

        // Valida si la cantidad comprada es mayor o igual al stock del ticket
        if (this.quantity >= this.stock) {

            return true;
        }

        // Fecha actual
        const now = new Date;
        // Se agrega 4 horas a la hora actual
        //now.setHours(now.getHours() + 4);

        // Valida si la fecha final del ticket ha caducado, comparando con la fecha actual + 4h
        const invalid = moment(this.saleDateEndAt).isBefore(now);
        if (invalid) {
            return true;
        }

        return false;
    }

    get isIncoming(): boolean {

        // Fecha actual
        const now = new Date;

        // Valida si el ticket aun no esta disponible para comprar
        const incoming = moment(now).isBefore(this.saleDateStartAt);

        return incoming;
    }

    //set the status of the ticket 

    get ticketStatus(): string {

        if (this.eventObj) {

            let currentDate = new Date();

            if (currentDate >= this.saleDateStartAt && currentDate <= this.saleDateEndAt && !(this.quantity >= this.stock && this.stock != 0 && this.quantity != 0)) {
                if (this.eventObj.stock - this.eventObj.quantity == 0 && this.eventObj.stock != 0
                    && (this.eventObj.type == EventType.CashlessWithExpirationDate || this.eventObj.type == EventType.CashlessWithOutExpirationDate ||
                        this.eventObj.type == EventType.EventWithExpirationDate || this.eventObj.type == EventType.EventWithOutExpirationDate
                    )
                )
                    return 'Sold Out'
                else
                    return 'Venta Activa' //dentro del rango de fechas
            }
            else if (currentDate > this.saleDateEndAt)
                return 'Fuera de período de venta' //end date pasado

            else if (this.quantity >= this.stock && this.stock != 0 && this.quantity != 0)
                return 'Sold Out' //agotado

            else if (currentDate < this.saleDateStartAt)
                return 'Programada' //start date no ha llegado
            else
                return 'Evento Inactivo'

        } else return 'Validation Required'

    }

    get customerCommission(): number {

        return (this.priceTotal) * (this.customerCommissionPercent / 100);
    }
}